// cspell:ignore ntile
import { assertNever } from "@redotech/util/type";
import type { CustomAnalyticsQueryAstFunctionName } from "../query-plan/custom-analytics-query-function-signatures";
import type { CustomAnalyticsQueryPlanCastType } from "../query-plan/custom-analytics-query-plan-primitives";
import type { CustomAnalyticsQueryTimeUnit } from "../shared/custom-analytics-query-primitives";

export const CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION = 1 as const;

export type CustomAnalyticsSemanticQueryVersion =
  typeof CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION;

export const AnalyticsValueType = {
  Array: "array",
  Boolean: "boolean",
  Date: "date",
  Datetime: "datetime",
  Id: "id",
  Money: "money",
  Number: "number",
  String: "string",
} as const;

export type AnalyticsValueType =
  (typeof AnalyticsValueType)[keyof typeof AnalyticsValueType];

export const AnalyticsGrain = {
  CheckoutVisit: "checkout_visit",
  CheckoutVisitAdvanced: "checkout_visit_advanced",
  CheckoutVisitTreatment: "checkout_visit_treatment",
  Treatment: "treatment",
  Split: "split",
  OrderShippingLine: "order_shipping_line",
  OrderRefundLineItem: "order_refund_line_item",
  CheckoutWidget: "checkout_widget",
  CheckoutWidgetAttributedOrderLineItems:
    "checkout_widget_attributed_order_line_items",
  OrderLineItemProperty: "order_line_item_property",
  OrderFulfillment: "order_fulfillment",
  Bill: "bill",
  BillingCharge: "billing_charge",
  OrderFulfillmentLineItem: "order_fulfillment_line_item",
  ReturnShipmentTrackingDetail: "return_shipment_tracking_detail",
  ReturnLineItemMultipleChoice: "return_line_item_multiple_choice",
  FulfillmentGroup: "fulfillment_group",
  FulfillmentGroupLineItem: "fulfillment_group_line_item",
  FulfillmentGroupShipment: "fulfillment_group_shipment",
  FulfillmentGroupShipmentSurcharge: "fulfillment_group_shipment_surcharge",
  Order: "order",
  OrderLineItem: "order_line_item",
  ExchangeOrder: "exchange_order",
  Return: "return",
  ReturnPaymentIntent: "return_payment_intent",
  ReturnLineItem: "return_line_item",
  ReturnShipment: "return_shipment",
  MarketingOutreachEvent: "marketing_outreach_event",
  Campaign: "campaign",
  Automation: "automation",
  PostPurchaseUpsellEvent: "post_purchase_upsell_event",
  PostPurchaseUpsellPage: "post_purchase_upsell_page",
  Product: "product",
  InventoryLevel: "inventory_level",
  StorageLocation: "storage_location",
  Location: "location",
  RescuePanelMetrics: "rescue_panel_metrics",
  RescuePanelAttributedOrders: "rescue_panel_attributed_orders",
  ShippingClaim: "shipping_claim",
  DraftReturnPendingReturnItem: "draft_return_pending_return_item",
  ReturnTag: "return_tag",
  SatisfactionResponse: "satisfaction_response",
  OrderExtendedWarranty: "order_extended_warranty",
  Conversation: "conversation",
  ConversationMessage: "conversation_message",
  FailedDiscountRescueEvent: "failed_discount_rescue_event",
  CustomerTag: "customer_tag",
  CustomCustomerEvent: "custom_customer_event",
  CustomerCustomField: "customer_custom_field",
  ProductTag: "product_tag",
  FulfillmentGroupTrackingEvent: "fulfillment_group_tracking_event",
  ProductCollection: "product_collection",
  ProductReview: "product_review",
  DailyShopperSession: "daily_shopper_session",
  MarketingAgentDecision: "marketing_agent_decision",
  MarketingAgentDecisionTag: "marketing_agent_decision_tag",
  CustomerGroupMembership: "customer_group_membership",
  CampaignRecipient: "campaign_recipient",
  CheckoutDeliveryOption: "checkout_delivery_option",
  CheckoutDeliveryGroup: "checkout_delivery_group",
  CheckoutInfoChange: "checkout_info_change",
  CheckoutShippingStepEvent: "checkout_shipping_step_event",
  CheckoutOrderCreatedEvent: "checkout_order_created_event",
  CheckoutWidgetInteraction: "checkout_widget_interaction",
  PostPurchaseSplitAssignment: "post_purchase_split_assignment",
  MarketingSubscriptionEvent: "marketing_subscription_event",
  BackInStockEvent: "back_in_stock_event",
  CollectionViewEvent: "collection_view_event",
  ProductViewEvent: "product_view_event",
  StorefrontCheckoutEvent: "storefront_checkout_event",
  CartChangeEvent: "cart_change_event",
  TryFirstPlan: "try_first_plan",
  TryFirstContract: "try_first_contract",
  ReturnAssessmentResponse: "return_assessment_response",
  ReturnAssessmentDefinitionField: "return_assessment_definition_field",
  ReturnAssessment: "return_assessment",
  TrackingPageEvent: "tracking_page_event",
  OrderTrackingPageOfferClick: "order_tracking_page_offer_click",
  OrderTrackingEmailEvent: "order_tracking_email_event",
  OrderTrackingDetail: "order_tracking_detail",
  InboundMessage: "inbound_message",
  MercuryShippingLine: "mercury_shipping_line",
  OrderPaymentGatewayName: "order_payment_gateway_name",
  Integration: "integration",
  Customer: "customer",
  SupportTicket: "support_ticket",
  SupportTicketMessage: "support_ticket_message",
  MessageTopicLink: "message_topic_link",
  SupportTicketTopic: "support_ticket_topic",
} as const;

export type AnalyticsGrain =
  (typeof AnalyticsGrain)[keyof typeof AnalyticsGrain];

export const AnalyticsAggregateOp = {
  Avg: "avg",
  BoolAnd: "bool_and",
  BoolOr: "bool_or",
  Count: "count",
  CountDistinct: "count_distinct",
  Max: "max",
  Median: "median",
  Min: "min",
  PercentileCont: "percentile_cont",
  PercentileDisc: "percentile_disc",
  Stddev: "stddev",
  Sum: "sum",
  Variance: "variance",
} as const;

export type AnalyticsAggregateOp =
  (typeof AnalyticsAggregateOp)[keyof typeof AnalyticsAggregateOp];

export function analyticsAggregateOpCollapsesOverIdentity(
  op: AnalyticsAggregateOp,
): boolean {
  switch (op) {
    case AnalyticsAggregateOp.Avg:
    case AnalyticsAggregateOp.BoolAnd:
    case AnalyticsAggregateOp.BoolOr:
    case AnalyticsAggregateOp.Count:
    case AnalyticsAggregateOp.CountDistinct:
    case AnalyticsAggregateOp.Max:
    case AnalyticsAggregateOp.Min:
    case AnalyticsAggregateOp.Sum:
      return true;
    case AnalyticsAggregateOp.Median:
    case AnalyticsAggregateOp.PercentileCont:
    case AnalyticsAggregateOp.PercentileDisc:
    case AnalyticsAggregateOp.Stddev:
    case AnalyticsAggregateOp.Variance:
      return false;
    default:
      return assertNever(op);
  }
}

export const AnalyticsRelativeOnlyOperation = {
  CumeDist: "cume_dist",
  DenseRank: "dense_rank",
  FirstValue: "first_value",
  Lag: "lag",
  LastValue: "last_value",
  Lead: "lead",
  NthValue: "nth_value",
  Ntile: "ntile",
  PercentRank: "percent_rank",
  Rank: "rank",
  RowNumber: "row_number",
} as const;

export type AnalyticsRelativeOnlyOperation =
  (typeof AnalyticsRelativeOnlyOperation)[keyof typeof AnalyticsRelativeOnlyOperation];

export type AnalyticsRelativeOperation =
  | AnalyticsAggregateOp
  | AnalyticsRelativeOnlyOperation;

export const ANALYTICS_RELATIVE_OPERATIONS: readonly AnalyticsRelativeOperation[] =
  [
    ...Object.values(AnalyticsAggregateOp),
    ...Object.values(AnalyticsRelativeOnlyOperation),
  ];

export const AnalyticsRelativeFrameMode = {
  Groups: "groups",
  Range: "range",
  Rows: "rows",
} as const;

export type AnalyticsRelativeFrameMode =
  (typeof AnalyticsRelativeFrameMode)[keyof typeof AnalyticsRelativeFrameMode];

export const AnalyticsRelativeBound = {
  UnboundedPreceding: "unbounded_preceding",
  Preceding: "preceding",
  CurrentRow: "current_row",
  Following: "following",
  UnboundedFollowing: "unbounded_following",
} as const;

export type AnalyticsRelativeBound =
  (typeof AnalyticsRelativeBound)[keyof typeof AnalyticsRelativeBound];

export const ANALYTICS_RELATIVE_FRAME_BOUND_ORDER = [
  AnalyticsRelativeBound.UnboundedPreceding,
  AnalyticsRelativeBound.Preceding,
  AnalyticsRelativeBound.CurrentRow,
  AnalyticsRelativeBound.Following,
  AnalyticsRelativeBound.UnboundedFollowing,
] as const satisfies readonly AnalyticsRelativeBound[];

export const AnalyticsRelativeNullOrdering = {
  First: "first",
  Last: "last",
} as const;

export type AnalyticsRelativeNullOrdering =
  (typeof AnalyticsRelativeNullOrdering)[keyof typeof AnalyticsRelativeNullOrdering];

export const AnalyticsRelativeNullTreatment = {
  Ignore: "ignore",
  Respect: "respect",
} as const;

export type AnalyticsRelativeNullTreatment =
  (typeof AnalyticsRelativeNullTreatment)[keyof typeof AnalyticsRelativeNullTreatment];

export const ANALYTICS_RELATIVE_RANGE_OFFSET_TIME_UNITS = [
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
] as const satisfies readonly CustomAnalyticsQueryTimeUnit[];

export type AnalyticsRelativeRangeOffsetTimeUnit =
  (typeof ANALYTICS_RELATIVE_RANGE_OFFSET_TIME_UNITS)[number];

const ANALYTICS_RELATIVE_RANGE_OFFSET_TIME_UNIT_KEYS: ReadonlySet<string> =
  new Set(ANALYTICS_RELATIVE_RANGE_OFFSET_TIME_UNITS);

export function isAnalyticsRelativeRangeOffsetTimeUnit(
  unit: CustomAnalyticsQueryTimeUnit,
): unit is AnalyticsRelativeRangeOffsetTimeUnit {
  return ANALYTICS_RELATIVE_RANGE_OFFSET_TIME_UNIT_KEYS.has(unit);
}

export const ANALYTICS_RELATIVE_MAX_WITHIN_TERMS = 5;

export const ANALYTICS_RELATIVE_MAX_SORT_TERMS = 5;

export const ANALYTICS_RELATIVE_MAX_ARGS = 3;

export const ANALYTICS_QUERY_MAX_RELATIVE_COLUMNS = 3;

export const ANALYTICS_QUERY_MAX_RELATIVE_FILTERS = 10;

/** Aggregates that collapse a one-to-many projection to one value per row. */
export const AnalyticsCollapseAggregate = {
  AnyValue: "any_value",
  BoolAnd: "booland_agg",
  BoolOr: "boolor_agg",
  Max: "max",
  Min: "min",
} as const;

export type AnalyticsCollapseAggregate =
  (typeof AnalyticsCollapseAggregate)[keyof typeof AnalyticsCollapseAggregate];

export const AnalyticsAggregateNullHandling = {
  Preserve: "preserve",
  Zero: "zero",
} as const;

export type AnalyticsAggregateNullHandling =
  (typeof AnalyticsAggregateNullHandling)[keyof typeof AnalyticsAggregateNullHandling];

export const AnalyticsFormulaNodeType = {
  Aggregate: "aggregate",
  Binary: "binary",
  MetricRef: "metric_ref",
} as const;

export type AnalyticsFormulaNodeType =
  (typeof AnalyticsFormulaNodeType)[keyof typeof AnalyticsFormulaNodeType];

export const AnalyticsConditionNodeType = {
  And: "and",
  Comparison: "comparison",
  Not: "not",
  Or: "or",
} as const;

export type AnalyticsConditionNodeType =
  (typeof AnalyticsConditionNodeType)[keyof typeof AnalyticsConditionNodeType];

export const AnalyticsColumnType = {
  Attribute: "attribute",
  Metric: "metric",
} as const;

export type AnalyticsColumnType =
  (typeof AnalyticsColumnType)[keyof typeof AnalyticsColumnType];

export const AnalyticsMetricColumnSource = {
  Catalog: "catalog",
  Expression: "expression",
  Formula: "formula",
} as const;

export type AnalyticsMetricColumnSource =
  (typeof AnalyticsMetricColumnSource)[keyof typeof AnalyticsMetricColumnSource];

/**
 * Attribute column authoring mode. `Catalog` is optional on stored queries,
 * which predate this discriminator, so absence means catalog.
 */
export const AnalyticsAttributeColumnSource = {
  Catalog: "catalog",
  Expression: "expression",
} as const;

export type AnalyticsAttributeColumnSource =
  (typeof AnalyticsAttributeColumnSource)[keyof typeof AnalyticsAttributeColumnSource];

export const AnalyticsScalarNodeType = {
  AttributeRef: "attribute_ref",
  Binary: "binary",
  Case: "case",
  Cast: "cast",
  Function: "function",
  Literal: "literal",
  Relative: "relative",
} as const;

export type AnalyticsScalarNodeType =
  (typeof AnalyticsScalarNodeType)[keyof typeof AnalyticsScalarNodeType];

export const AnalyticsScalarFunctionName = {
  Abs: "abs",
  Ceil: "ceil",
  CharLength: "char_length",
  Coalesce: "coalesce",
  Concat: "concat",
  CurrentDate: "current_date",
  CurrentTimestamp: "current_timestamp",
  DateAdd: "date_add",
  DateDiff: "date_diff",
  DateTrunc: "date_trunc",
  Extract: "extract",
  Floor: "floor",
  Greatest: "greatest",
  LastDay: "last_day",
  Least: "least",
  Lower: "lower",
  Lpad: "lpad",
  Mod: "mod",
  NullIf: "nullif",
  Power: "power",
  Replace: "replace",
  Right: "right",
  Round: "round",
  Rpad: "rpad",
  Sign: "sign",
  SplitPart: "split_part",
  Substring: "substring",
  Trim: "trim",
  Upper: "upper",
} as const satisfies Record<string, CustomAnalyticsQueryAstFunctionName>;

export type AnalyticsScalarFunctionName =
  (typeof AnalyticsScalarFunctionName)[keyof typeof AnalyticsScalarFunctionName];

/** `json` is deliberately absent: a variant is not a dimension value. */
export const AnalyticsScalarCastType = {
  Boolean: "boolean",
  Date: "date",
  Number: "number",
  String: "string",
  Timestamp: "timestamp",
} as const satisfies Record<string, CustomAnalyticsQueryPlanCastType>;

export type AnalyticsScalarCastType =
  (typeof AnalyticsScalarCastType)[keyof typeof AnalyticsScalarCastType];

export const ANALYTICS_SCALAR_EXPRESSION_MAX_NODES = 64;

export const ANALYTICS_SCALAR_EXPRESSION_MAX_DEPTH = 6;

export const ANALYTICS_POST_AGGREGATE_MAX_REFERENCE_DEPTH = 3;

/** Each reference inlines the referenced output's whole tree, so chaining multiplies. */
export const ANALYTICS_POST_AGGREGATE_MAX_LOWERED_NODES = 512;

export const AnalyticsTimeBoundaryType = {
  AbsoluteDate: "absolute_date",
  CurrentDate: "current_date",
  RelativeDate: "relative_date",
} as const;

export type AnalyticsTimeBoundaryType =
  (typeof AnalyticsTimeBoundaryType)[keyof typeof AnalyticsTimeBoundaryType];

export const AnalyticsRelativeTimeAnchor = {
  Current: "current",
  StartOf: "start_of",
} as const;

export type AnalyticsRelativeTimeAnchor =
  (typeof AnalyticsRelativeTimeAnchor)[keyof typeof AnalyticsRelativeTimeAnchor];

export const AnalyticsFormulaBinaryOperation = {
  Add: "add",
  Divide: "divide",
  Multiply: "multiply",
  Subtract: "subtract",
} as const;

export type AnalyticsFormulaBinaryOperation =
  (typeof AnalyticsFormulaBinaryOperation)[keyof typeof AnalyticsFormulaBinaryOperation];
